import React from 'react'
import { Play, Square, FileText } from 'lucide-react'
import { Project } from '../../types'
import ProjectSelector from './ProjectSelector'

interface TimerOperationCardProps {
  // 計時器狀態
  isRunning: boolean
  isLoading: boolean
  
  // 工作描述
  description: string
  onDescriptionChange: (value: string) => void
  
  // 專案相關
  projects: Project[]
  selectedProject: Project | null
  recentFavoriteProjects: Project[]
  onSelectProject: (project: Project | null) => void
  onReloadProjects?: () => Promise<void>
  
  // 控制按鈕
  onStart: () => void
  onStop: () => void
  
  // 狀態
  disabled?: boolean
  className?: string
}

const TimerOperationCard: React.FC<TimerOperationCardProps> = ({
  isRunning,
  isLoading,
  description,
  onDescriptionChange,
  projects,
  selectedProject,
  recentFavoriteProjects,
  onSelectProject,
  onReloadProjects,
  onStart,
  onStop,
  disabled = false,
  className = ''
}) => {
  return (
    <div className={`card p-4 md:p-6 space-y-4 md:space-y-6 ${className} shadow-lg hover:shadow-xl transition-shadow duration-300`}>
      <div className="flex items-center space-x-2 mb-2 md:mb-4">
        <div className="w-1 h-5 md:h-6 bg-gradient-to-b from-primary-500 to-primary-600 rounded-full"></div>
        <h3 className="text-base md:text-lg font-semibold text-secondary-800">計時操作</h3>
      </div>
      
      {/* 任務名稱輸入 */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-secondary-700">
          任務名稱
        </label>
        
        <div className="relative group">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FileText className={`w-5 h-5 transition-colors duration-200 ${description ? 'text-primary-500' : 'text-secondary-400'} group-focus-within:text-primary-500`} />
          </div>
          
          <input
            type="text"
            value={description}
            onChange={(e) => onDescriptionChange(e.target.value)}
            disabled={disabled}
            placeholder="輸入任務名稱..."
            className={`
              w-full pl-10 pr-4 py-3
              bg-white border border-secondary-300 rounded-lg
              text-secondary-900 placeholder-secondary-500
              focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500
              focus:bg-primary-50/30
              disabled:bg-secondary-50 disabled:text-secondary-500 disabled:cursor-not-allowed
              transition-all duration-200
              transform focus:scale-[1.02]
            `}
          />
          
          {/* 輸入提示 */}
          {description.length > 0 && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <div className="text-xs text-secondary-400 bg-secondary-100 px-2 py-1 rounded-full">
                {description.length}
              </div>
            </div>
          )}
        </div>
        
        {/* 快速輸入建議 */}
        {description.length === 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {['開發功能', '會議討論', '測試驗證', '文件撰寫', '代碼審核'].map((suggestion) => (
              <button
                key={suggestion}
                onClick={() => onDescriptionChange(suggestion)}
                disabled={disabled}
                className={`
                  px-3 py-1.5 text-sm font-medium
                  bg-gradient-to-r from-primary-100 to-primary-200
                  hover:from-primary-200 hover:to-primary-300
                  text-primary-700 rounded-full
                  transition-all duration-200
                  transform hover:scale-105 active:scale-95
                  disabled:opacity-50 disabled:cursor-not-allowed
                  shadow-sm hover:shadow-md
                  border border-primary-200/50
                `}
              >
                {suggestion}
              </button>
            ))}
          </div>
        )}
      </div>
      
      {/* 專案選擇 */}
      <ProjectSelector
        projects={projects}
        selectedProject={selectedProject}
        recentFavoriteProjects={recentFavoriteProjects}
        onSelectProject={onSelectProject}
        onReloadProjects={onReloadProjects}
        disabled={isRunning || disabled}
      />
      
      {/* 分隔線 */}
      <div className="relative">
        <div className="border-t border-secondary-200"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="bg-white px-4">
            <div className="w-2 h-2 bg-secondary-300 rounded-full"></div>
          </div>
        </div>
      </div>
      
      {/* 控制按鈕 */}
      <div className="flex items-center justify-center space-x-4">
        {!isRunning ? (
          // 開始按鈕
          <button
            onClick={onStart}
            disabled={disabled || isLoading || !selectedProject}
            className={`
              relative group
              flex items-center justify-center space-x-3
              px-10 py-4 rounded-xl
              bg-gradient-to-r from-green-500 to-green-600
              hover:from-green-600 hover:to-green-700
              text-white font-semibold text-lg
              shadow-lg hover:shadow-xl
              transform hover:scale-105 active:scale-95
              transition-all duration-200
              disabled:opacity-50 disabled:cursor-not-allowed
              disabled:hover:scale-100 disabled:hover:shadow-lg
              focus:outline-none focus:ring-4 focus:ring-green-300
              overflow-hidden
            `}
            title="開始計時"
          >
            {/* 按鈕內光澤效果 */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
            
            {isLoading ? (
              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Play className="w-6 h-6 drop-shadow-sm" />
            )}
            <span className="relative z-10">開始計時</span>
          </button>
        ) : (
          // 停止按鈕
          <button
            onClick={onStop}
            disabled={isLoading}
            className={`
              relative group
              flex items-center justify-center space-x-3
              px-10 py-4 rounded-xl
              bg-gradient-to-r from-red-500 to-red-600
              hover:from-red-600 hover:to-red-700
              text-white font-semibold text-lg
              shadow-lg hover:shadow-xl
              transform hover:scale-105 active:scale-95
              transition-all duration-200
              disabled:opacity-50 disabled:cursor-not-allowed
              disabled:hover:scale-100 disabled:hover:shadow-lg
              focus:outline-none focus:ring-4 focus:ring-red-300
              overflow-hidden
            `}
            title="停止計時"
          >
            {/* 按鈕內光澤效果 */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent transform -skew-x-12 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>
            
            {isLoading ? (
              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Square className="w-6 h-6 drop-shadow-sm" />
            )}
            <span className="relative z-10">停止計時</span>
          </button>
        )}
      </div>
      
      {/* 狀態提示 */}
      {!selectedProject && !isLoading && (
        <div className="text-center p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <div className="flex items-center justify-center space-x-2">
            <div className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-yellow-800">
              請先選擇專案才能開始計時
            </span>
          </div>
        </div>
      )}
      
      {isLoading && (
        <div className="text-center p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-center space-x-3">
            <div className="flex space-x-1">
              <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce"></div>
              <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
              <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            </div>
            <span className="text-sm font-medium text-blue-800">
              處理中...
            </span>
          </div>
        </div>
      )}
    </div>
  )
}

export default TimerOperationCard
